'use client';

import { Message } from '@/lib/copilot/message';
import classNames from 'classnames';
import { useState, ReactNode, memo } from 'react';
import { useChat } from '@/contexts/ChatProvider';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

type UserMessageProps = {
  message: Message;
  isLast: boolean;
};

type UserMessageOptionsProps = {
  children: ReactNode;
  isLast: boolean;
};

const UserMessageOptions = ({ children, isLast }: UserMessageOptionsProps) => {
  const [retried, setRetry] = useState<boolean>(false);
  const { status, retry } = useChat();

  if (status === 'in_progress' || !isLast) {
    return <>{children}</>;
  }

  const handleRetry = () => {
    retry();
    setRetry(true);
  };

  const buttonClass = 'p-2 rounded-lg transition-all duration-300 hover:bg-gray-400';

  return (
    <div className="flex flex-col gap-2 group relative">
      {children}
      <div className="flex ml-auto group-hover:flex bg-white rounded-lg border border-gray-600 p-1 items-center gap-2 text-navy-600 shadow w-fit">
        <button 
          title="Retry" 
          onClick={handleRetry} 
          className={classNames(buttonClass, 'flex items-center gap-2')}
        >
          <ArrowPathIcon 
            className={classNames('w-5', retried ? 'animate-spin' : '')} 
            title="Retry" 
            aria-hidden="true" 
          />
          <span className="">Retry</span>
        </button>
      </div>
    </div>
  );
};

/**
 * Component that renders user messages and provides retry functionality
 */
function UserMessageComponent({ message, isLast }: UserMessageProps) {
  return (
    <UserMessageOptions isLast={isLast}>
      <div 
        aria-label="User message"
        className={classNames(
          'ml-auto bg-blue-400 text-navy-600',
          'p-4 rounded-lg w-fit'
        )}
      >
        <p className="sr-only">User: </p>
        {message.content}
      </div>
    </UserMessageOptions>
  );
}

export const UserMessage = memo(UserMessageComponent);